import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, Users, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rs_admin_token";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminVisitors = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
    } catch {
      toast.error("Incorrect passcode.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/visitors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        logout();
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    load();
    const id = setInterval(load, 10000); // real-time-ish polling every 10s
    return () => clearInterval(id);
  }, [token, load]);

  // ---- Passcode gate ----
  if (!token) {
    return (
      <main data-testid="admin-login" className="min-h-screen bg-[#0a0908] flex items-center justify-center px-6 grain relative">
        <form onSubmit={login} className="w-full max-w-sm text-center">
          <Lock className="mx-auto text-[#c5a059]" size={26} />
          <h1 className="font-serif text-3xl text-white font-light mt-6">Visitor Log</h1>
          <p className="text-white/40 text-sm font-light mt-2">Enter the admin passcode to continue.</p>
          <input
            data-testid="admin-passcode"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            aria-label="Admin passcode"
            className="w-full mt-8 bg-transparent border-b border-white/20 py-3 text-center text-white placeholder:text-white/30 text-sm tracking-[0.2em] focus:outline-none focus:border-[#c5a059] transition-colors"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full mt-8 bg-[#c5a059] text-[#0a0908] py-3.5 text-[11px] font-semibold tracking-[0.25em] uppercase hover:bg-[#b08d4a] transition-colors disabled:opacity-60"
          >
            {loading ? "Verifying…" : "Enter"}
          </button>
        </form>
      </main>
    );
  }

  // ---- Dashboard ----
  const visitors = data?.visitors || [];
  return (
    <main data-testid="admin-visitors" className="min-h-screen bg-[#0a0908] pt-28 pb-24 grain relative">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Private · Live</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white font-light mt-3">Visitor Log</h1>
            <p className="text-white/40 text-sm font-light mt-2">Auto-refreshes every 10 seconds. Each row is a unique IP address.</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load}
              className="flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-white/40 px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout}
              className="flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-white/40 px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors">
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 md:gap-6 mt-10">
          <div className="border border-white/10 bg-[#161412] p-7">
            <Users className="text-[#c5a059]" size={20} />
            <p className="font-serif text-5xl text-white mt-4" data-testid="stat-unique">{data?.unique ?? "—"}</p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mt-2">Unique Visitors (IPs)</p>
          </div>
          <div className="border border-white/10 bg-[#161412] p-7">
            <Eye className="text-[#c5a059]" size={20} />
            <p className="font-serif text-5xl text-white mt-4" data-testid="stat-total">{data?.total_visits ?? "—"}</p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mt-2">Total Page Views</p>
          </div>
        </div>

        {/* Table */}
        <div className="mt-10 border border-white/10">
          <div className="hidden sm:grid grid-cols-12 bg-[#161412] text-[10px] tracking-[0.2em] uppercase text-white/50 px-6 py-4">
            <div className="col-span-4">IP Address</div>
            <div className="col-span-3">First Seen</div>
            <div className="col-span-3">Last Seen</div>
            <div className="col-span-2 text-right">Visits</div>
          </div>
          {visitors.length === 0 && (
            <p className="text-white/40 text-sm font-light px-6 py-10 text-center" data-testid="admin-empty">
              No visits recorded yet.
            </p>
          )}
          {visitors.map((v) => (
            <div key={v.ip} data-testid={`visitor-row-${v.ip}`}
              className="grid grid-cols-2 sm:grid-cols-12 gap-y-1 px-6 py-4 border-t border-white/10 hover:bg-white/[0.03] transition-colors">
              <div className="col-span-2 sm:col-span-4 font-mono text-sm text-white">{v.ip}</div>
              <div className="col-span-1 sm:col-span-3 text-white/60 text-sm font-light">{fmt(v.first_seen)}</div>
              <div className="col-span-1 sm:col-span-3 text-white/60 text-sm font-light">{fmt(v.last_seen)}</div>
              <div className="col-span-2 sm:col-span-2 text-right font-serif text-lg text-[#c5a059]">{v.count}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default AdminVisitors;
